import { useEffect, useRef, useState } from "react";
import { Play, Pause, X, Video, Headphones } from "lucide-react";
import { IMAGES, isSpecialEpisode } from "@/data/content";

export default function FloatingAudioPlayer({ nowPlaying, isPlaying, onToggle, onClose }) {
  const [progress, setProgress] = useState(0);
  const [realDuration, setRealDuration] = useState(0);
  const [mode, setMode] = useState("listen");
  const epAudioRef = useRef(null);
  const epVideoRef = useRef(null);
  const pendingSeek = useRef(null);
  const timer = useRef(null);
  const hasRealAudio = !!nowPlaying?.audio_url;
  const canWatch = !!nowPlaying?.video_url;

  useEffect(() => {
    setProgress(0);
    setRealDuration(0);
    setMode("listen");
    pendingSeek.current = null;
  }, [nowPlaying?.number, nowPlaying?.audio_url]);

  const activeEl = () => (mode === "watch" && canWatch ? epVideoRef.current : epAudioRef.current);

  useEffect(() => {
    const el = activeEl();
    if (!el || !hasRealAudio) return;
    if (isPlaying) {
      el.play().catch(() => {});
      if (mode !== "watch") window.dispatchEvent(new Event("cgd:episode-audio"));
    } else {
      el.pause();
    }
  }, [isPlaying, hasRealAudio, nowPlaying?.number, mode, canWatch]);

  // Seamless WATCH <-> LISTEN: hand off the exact second and keep rolling.
  useEffect(() => {
    if (pendingSeek.current == null) return;
    const el = activeEl();
    if (!el) return;
    const t = pendingSeek.current;
    pendingSeek.current = null;
    const apply = () => {
      try {
        el.currentTime = t;
      } catch (e) {}
      if (isPlaying) el.play().catch(() => {});
    };
    if (el.readyState >= 1) apply();
    else el.addEventListener("loadedmetadata", apply, { once: true });
  }, [mode, canWatch, isPlaying]);

  const switchMode = (next) => {
    if (next === mode) return;
    const el = activeEl();
    pendingSeek.current = el?.currentTime || 0;
    el?.pause();
    setMode(next);
  };

  useEffect(() => {
    const els = [epAudioRef.current, epVideoRef.current].filter(Boolean);
    if (!els.length) return;
    const onTime = () => {
      const el = activeEl();
      if (el) setProgress(el.duration ? el.currentTime / el.duration : 0);
    };
    const onMeta = (e) => setRealDuration(e.target.duration || 0);
    const onEnd = () => {
      setProgress(0);
      window.dispatchEvent(new Event("cgd:episode-ended"));
    };
    els.forEach((el) => {
      el.addEventListener("timeupdate", onTime);
      el.addEventListener("loadedmetadata", onMeta);
      el.addEventListener("ended", onEnd);
    });
    return () => {
      els.forEach((el) => {
        el.removeEventListener("timeupdate", onTime);
        el.removeEventListener("loadedmetadata", onMeta);
        el.removeEventListener("ended", onEnd);
      });
    };
  }, [hasRealAudio, canWatch, nowPlaying?.number, mode]);

  useEffect(() => {
    if (isPlaying && nowPlaying && !hasRealAudio) {
      timer.current = setInterval(() => {
        setProgress((p) => (p >= 1 ? 0 : p + 0.0022));
      }, 500);
    }
    return () => clearInterval(timer.current);
  }, [isPlaying, nowPlaying, hasRealAudio]);

  if (!nowPlaying) return null;

  const hm = (nowPlaying.duration || "").match(/(\d+)\s*hr/);
  const mm = (nowPlaying.duration || "").match(/(\d+)\s*min/);
  const fallbackSecs = ((hm ? +hm[1] * 60 : 0) + (mm ? +mm[1] : 0)) * 60;
  const totalSecs = realDuration || fallbackSecs;
  const fmtT = (secs) => {
    if (!secs) return "";
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = Math.floor(secs % 60);
    return h
      ? `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
      : `${m}:${String(s).padStart(2, "0")}`;
  };

  return (
    <div
      data-testid="audio-player-bar"
      className="fixed inset-x-0 bottom-0 z-50 border-t border-white/10 glass"
    >
      {mode === "watch" && canWatch && (
        <div
          data-testid="watch-panel"
          className="absolute bottom-full right-4 z-50 mb-3 w-44 overflow-hidden rounded-2xl border border-white/15 shadow-[0_10px_40px_rgba(0,0,0,0.65)] sm:w-60"
        >
          <video
            ref={epVideoRef}
            src={nowPlaying.video_url}
            preload="metadata"
            playsInline
            controls
            className="w-full"
          />
        </div>
      )}
      <div className="mx-auto flex max-w-7xl items-center gap-4 px-4 py-3 sm:gap-5 sm:px-6 lg:px-8">
        {hasRealAudio && (
          <audio ref={epAudioRef} src={nowPlaying.audio_url} preload="metadata" />
        )}
        <img
          src={nowPlaying?.art_url || IMAGES.cover}
          alt={nowPlaying?.title ? `${nowPlaying.title} art` : "City Girls Decoded cover art"}
          className={`h-10 w-10 shrink-0 rounded-full object-cover shadow-[0_0_20px_rgba(255,0,85,0.5)] ${
            isPlaying ? "animate-spin-slow" : ""
          }`}
          style={{ animationPlayState: isPlaying ? "running" : "paused" }}
        />

        <div className="min-w-0 flex-1">
          <p data-testid="now-playing-label" className="truncate text-xs font-bold sm:text-sm">
            <span className="mr-2 text-cherry">
              {isSpecialEpisode(nowPlaying) ? "CGD: IRL" : `EP ${nowPlaying.number}`}
            </span>
            {nowPlaying.title}
          </p>
          <div className="mt-1.5 flex items-center gap-3">
            <span className="w-12 text-right text-[10px] tabular-nums text-white/40">
              {fmtT(Math.round(totalSecs * progress))}
            </span>
            <div className="h-1 flex-1 overflow-hidden rounded-full bg-white/10">
              <div
                className="h-full rounded-full bg-cherry transition-[width] duration-500 ease-linear"
                style={{ width: `${progress * 100}%` }}
              />
            </div>
            <span className="w-12 text-[10px] tabular-nums text-white/40">{fmtT(totalSecs)}</span>
          </div>
        </div>

        <div className="hidden items-end gap-[3px] sm:flex" aria-hidden="true">
          {[0, 1, 2, 3, 4].map((i) => (
            <span
              key={i}
              className={`w-[3px] rounded-full bg-cherry ${isPlaying ? "animate-eq" : ""}`}
              style={{ height: 14 + (i % 3) * 6, animationDelay: `${i * 0.13}s`, animationPlayState: isPlaying ? "running" : "paused", transform: isPlaying ? undefined : "scaleY(0.3)" }}
            />
          ))}
        </div>

        {canWatch && (
          <div className="flex shrink-0 items-center rounded-full border border-white/15 p-0.5">
            <button
              data-testid="player-watch-btn"
              onClick={() => switchMode("watch")}
              className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-wider transition-colors ${
                mode === "watch" ? "bg-cherry text-ink" : "text-white/60 hover:text-white"
              }`}
            >
              <Video className="h-3.5 w-3.5" /> Watch
            </button>
            <button
              data-testid="player-listen-btn"
              onClick={() => switchMode("listen")}
              className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-wider transition-colors ${
                mode === "listen" ? "bg-cherry text-ink" : "text-white/60 hover:text-white"
              }`}
            >
              <Headphones className="h-3.5 w-3.5" /> Listen
            </button>
          </div>
        )}

        <button
          data-testid="audio-player-toggle"
          onClick={onToggle}
          aria-label={isPlaying ? "Pause" : "Play"}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white text-ink transition-transform duration-300 hover:scale-105"
        >
          {isPlaying ? <Pause className="h-4 w-4 fill-current" /> : <Play className="ml-0.5 h-4 w-4 fill-current" />}
        </button>
        <button
          data-testid="audio-player-close"
          onClick={onClose}
          aria-label="Close player"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/15 text-white/60 transition-colors hover:border-cherry hover:text-cherry"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
